import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  IconActivityHeartbeat,
  IconAlertTriangle,
  IconCalendarEvent,
  IconChartLine,
  IconCircleCheck,
  IconFlag,
  IconTrendingDown,
  IconTrendingUp,
  IconUserCheck,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import "./HomePage.css";

// Mirrors the iOS home (ManagerHomeViewModel): every source is best-effort, so
// one failing endpoint leaves its section empty rather than blanking the page.
// Roles a route would 403 are never sent the request.
const LEADS_ROLES = ["MANAGER", "COMPANY_ADMIN", "REPPI_ADMIN"];
const MEETINGS_ROLES = ["MANAGER", "COMPANY_ADMIN", "REPPI_ADMIN"];
const TEAM_STATS_ROLES = ["MANAGER", "COMPANY_ADMIN", "REPPI_ADMIN"];
const TASKS_ROLES = ["MANAGER", "REP"];

const MAX_EVENTS = 6;
const MAX_UPDATES = 4;
const MAX_MOMENTUM = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

const fullName = person =>
  [person?.first_name, person?.last_name].filter(Boolean).join(" ").trim();

const formatNumber = value => Number(value || 0).toLocaleString("en-US");

const startOfDay = date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// "YYYY-MM-DD" parsed as a local date, not UTC midnight.
const parseLocalDate = value => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const parseDate = value => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const dayLabel = date =>
  startOfDay(date).getTime() === startOfDay(new Date()).getTime()
    ? "Today"
    : date.toLocaleDateString("en-US", { weekday: "short" });

const formatShortDate = date =>
  date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

const formatMinute = minute => {
  const d = new Date();
  d.setHours(Math.floor(minute / 60), minute % 60, 0, 0);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const taskTitle = task => {
  const firstLine = (task.task_details || "").split("\n")[0].trim();
  if (!firstLine) return "Untitled task";
  return firstLine.length > 60 ? `${firstLine.slice(0, 57)}…` : firstLine;
};

const isOpenTask = task =>
  !task.completed_at && task.acceptance_status !== "rejected";

function HomePage() {
  const { getVerifiedJWT, user } = useAuth();
  const role = user?.role;
  const myRepId = user?.repId;

  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [events, setEvents] = useState(null);
  const [meetings, setMeetings] = useState(null);
  const [tasks, setTasks] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      };

      // Resolves to the response's `data`, or null when the request fails.
      const get = async path => {
        try {
          const response = await fetch(getApiEndpoint(path), { headers });
          if (!response.ok) return null;
          const result = await response.json();
          return result?.data ?? null;
        } catch {
          return null;
        }
      };
      const getIf = (roles, path) =>
        roles.includes(role) ? get(path) : Promise.resolve(null);

      const [leadsData, statsData, eventsData, meetingsData, tasksData] =
        await Promise.all([
          getIf(LEADS_ROLES, "/v1/recruiting/leads"),
          getIf(TEAM_STATS_ROLES, "/v1/revenue/team-stats"),
          get("/v2/calendar-events"),
          getIf(MEETINGS_ROLES, "/v1/recruiting/upcoming-meetings"),
          getIf(TASKS_ROLES, "/v1/custom-task/list"),
        ]);

      setLeads(Array.isArray(leadsData) ? leadsData : null);
      setTeamStats(statsData);
      setEvents(Array.isArray(eventsData) ? eventsData : null);
      setMeetings(Array.isArray(meetingsData) ? meetingsData : null);
      setTasks(tasksData);
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT, role]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const allLeads = leads || [];
    const total = allLeads.length;
    const signed = allLeads.filter(lead => lead.status === "Signed").length;
    const weekAgo = Date.now() - 7 * DAY_MS;
    const newThisWeek = allLeads.filter(
      lead => (parseDate(lead.created_at)?.getTime() ?? 0) >= weekAgo
    ).length;
    const team = teamStats?.team;

    return [
      {
        id: "leads",
        label: "Total Leads",
        value: formatNumber(total),
        delta: newThisWeek > 0 ? `+${newThisWeek} wk` : null,
        icon: IconChartLine,
      },
      {
        id: "signed",
        label: "Total Signed",
        value: formatNumber(signed),
        delta: total > 0 ? `${Math.round((signed / total) * 100)}% conv` : null,
        icon: IconUserCheck,
      },
      {
        id: "active",
        label: "Active Reps",
        value: formatNumber(team?.reps_active),
        delta: team ? `${formatNumber(team.reps_total)} total` : null,
        icon: IconActivityHeartbeat,
      },
    ];
  }, [leads, teamStats]);

  const myOpenTasks = useMemo(
    () =>
      (tasks?.assigned_tasks || []).filter(
        task =>
          isOpenTask(task) &&
          myRepId != null &&
          task.assigned_user?.rep_id === myRepId
      ),
    [tasks, myRepId]
  );

  const upcoming = useMemo(() => {
    const today = startOfDay(new Date());
    const weekEnd = new Date(today.getTime() + 7 * DAY_MS);
    const inWindow = date => date && date >= today && date <= weekEnd;
    const entries = [];

    (events || []).forEach(event => {
      const date = parseLocalDate(event.event_date);
      if (!inWindow(date)) return;
      const time = event.is_all_day
        ? "All day"
        : formatMinute(event.start_minute);
      entries.push({
        key: `event-${event.id}`,
        date: event.is_all_day
          ? date
          : new Date(date.getTime() + (event.start_minute || 0) * 60000),
        tag: event.category || "event",
        title: event.title,
        detail: event.location ? `${time} · ${event.location}` : time,
      });
    });

    (meetings || []).forEach(meeting => {
      const date = parseDate(meeting.time);
      if (!inWindow(date)) return;
      entries.push({
        key: `meeting-${meeting.id}`,
        date,
        tag: "recruiting",
        title: `Meeting · ${meeting.recruitName || "Recruit"}`,
        detail: date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
        }),
      });
    });

    myOpenTasks.forEach(task => {
      const date = parseDate(task.due_date);
      if (!inWindow(date)) return;
      entries.push({
        key: `task-${task.id}`,
        date,
        tag: "task due",
        title: taskTitle(task),
        detail: formatShortDate(date),
      });
    });

    return entries
      .sort((a, b) => a.date - b.date)
      .slice(0, MAX_EVENTS)
      .map(entry => ({ ...entry, day: dayLabel(entry.date) }));
  }, [events, meetings, myOpenTasks]);

  const updates = useMemo(() => {
    const items = [];
    const surfaced = new Set();
    const flags = teamStats?.flags || [];
    const reps = teamStats?.reps || [];

    flags.slice(0, MAX_UPDATES).forEach(flag => {
      const issue =
        flag.issues?.find(i => i.severity === "bad") || flag.issues?.[0];
      if (!issue) return;
      surfaced.add(flag.reppi_user_id);
      items.push({
        key: `flag-${flag.reppi_user_id}`,
        tone: issue.severity === "bad" ? "bad" : "warn",
        icon: issue.code === "no_sales" ? IconTrendingDown : IconAlertTriangle,
        tag: issue.label,
        who: fullName(flag),
        text: ` — ${issue.detail}`,
      });
    });

    reps
      .filter(rep => rep.trend === "down" && !surfaced.has(rep.reppi_user_id))
      .slice(0, Math.max(0, MAX_UPDATES - items.length))
      .forEach(rep => {
        surfaced.add(rep.reppi_user_id);
        items.push({
          key: `down-${rep.reppi_user_id}`,
          tone: "warn",
          icon: IconTrendingDown,
          tag: "Slowing down",
          who: fullName(rep),
          text: "'s activity is trending down.",
        });
      });

    const now = new Date();
    [...myOpenTasks]
      .sort((a, b) => {
        const aDue = parseDate(a.due_date)?.getTime() ?? Infinity;
        const bDue = parseDate(b.due_date)?.getTime() ?? Infinity;
        return aDue - bDue;
      })
      .slice(0, MAX_UPDATES)
      .forEach(task => {
        const due = parseDate(task.due_date);
        const overdue = due && due < now;
        items.push({
          key: `task-${task.id}`,
          tone: overdue ? "bad" : "warn",
          icon: overdue ? IconAlertTriangle : IconFlag,
          tag: overdue ? "Overdue task" : "Open task",
          who: fullName(task.created_by_user) || "Task",
          text: ` — ${taskTitle(task)}`,
          sub: due
            ? `${overdue ? "was due" : "due"} ${formatShortDate(due)}`
            : null,
        });
      });

    const weekAgo = Date.now() - 7 * DAY_MS;
    (tasks?.created_tasks || [])
      .filter(task => (parseDate(task.completed_at)?.getTime() ?? 0) >= weekAgo)
      .sort((a, b) => parseDate(b.completed_at) - parseDate(a.completed_at))
      .slice(0, MAX_UPDATES)
      .forEach(task => {
        items.push({
          key: `done-${task.id}`,
          tone: "good",
          icon: IconCircleCheck,
          tag: "Completed",
          who: fullName(task.assigned_user) || "Someone",
          text: ` completed "${taskTitle(task)}".`,
        });
      });

    reps
      .filter(
        rep => rep.status === "not_out" && !surfaced.has(rep.reppi_user_id)
      )
      .slice(0, 3)
      .forEach(rep => {
        items.push({
          key: `notout-${rep.reppi_user_id}`,
          tone: "muted",
          icon: IconActivityHeartbeat,
          tag: "Not out",
          who: fullName(rep),
          text: " hasn't knocked this period.",
        });
      });

    return items;
  }, [teamStats, myOpenTasks, tasks]);

  const momentum = useMemo(() => {
    const reps = teamStats?.reps || [];
    // Without a date window the server's `trend` stays flat, so rising is read
    // off the 7-day sparkline: second half beating the first half.
    return reps
      .map(rep => {
        const spark = rep.sparkline || [];
        const mid = Math.floor(spark.length / 2);
        const sum = list => list.reduce((acc, n) => acc + Number(n || 0), 0);
        const delta =
          spark.length >= 2
            ? sum(spark.slice(mid)) - sum(spark.slice(0, mid))
            : 0;
        return { rep, delta, weekSold: sum(spark) };
      })
      .filter(
        ({ rep, delta, weekSold }) =>
          rep.trend === "up" || (delta > 0 && weekSold > 0)
      )
      .sort((a, b) => b.delta - a.delta || b.weekSold - a.weekSold)
      .slice(0, MAX_MOMENTUM)
      .map(({ rep, delta, weekSold }) => ({
        key: `up-${rep.reppi_user_id}`,
        tone: "good",
        icon: IconTrendingUp,
        tag: "Momentum",
        who: fullName(rep),
        text: " is trending up.",
        sub:
          delta > 0
            ? `+${delta} vs earlier this week · ${weekSold} signs this week`
            : `${weekSold} signs this week`,
      }));
  }, [teamStats]);

  return (
    <div className="home-page">
      <div className="home-stats">
        {stats.map(stat => {
          const Icon = stat.icon;
          return (
            <div key={stat.id} className="home-stat-card">
              <span className="home-stat-icon" aria-hidden="true">
                <Icon size={18} stroke={1.75} />
              </span>
              <span className="home-stat-value">
                {loading && !leads && !teamStats ? "–" : stat.value}
              </span>
              <span className="home-stat-label">{stat.label}</span>
              {stat.delta && (
                <span className="home-stat-delta">{stat.delta}</span>
              )}
            </div>
          );
        })}
      </div>

      <HomeSection
        title="Upcoming Events"
        meta="this week"
        loading={loading}
        emptyText="Nothing scheduled in the next 7 days."
      >
        {upcoming.map(event => (
          <li key={event.key} className="home-row">
            <span className="home-row-icon home-tone-muted" aria-hidden="true">
              <IconCalendarEvent size={16} stroke={1.75} />
            </span>
            <div className="home-row-body">
              <span className="home-row-tag">{event.tag}</span>
              <span className="home-row-text">
                <strong>{event.title}</strong>
              </span>
              <span className="home-row-sub">{event.detail}</span>
            </div>
            <span className="home-row-meta">{event.day}</span>
          </li>
        ))}
      </HomeSection>

      <HomeSection
        title="Updates"
        meta={updates.length}
        loading={loading}
        emptyText="No open tasks or flagged reps right now."
      >
        {updates.map(item => (
          <UpdateRow key={item.key} item={item} />
        ))}
      </HomeSection>

      <HomeSection
        title="Momentum"
        meta={momentum.length}
        loading={loading}
        emptyText="No reps trending up this week yet."
      >
        {momentum.map(item => (
          <UpdateRow key={item.key} item={item} />
        ))}
      </HomeSection>
    </div>
  );
}

function HomeSection({ title, meta, loading, emptyText, children }) {
  const rows = React.Children.toArray(children);
  return (
    <section className="home-section">
      <header className="home-section-header">
        <h2 className="home-section-title">{title}</h2>
        <span className="home-section-meta">{meta}</span>
      </header>
      <div className="home-card">
        {rows.length > 0 ? (
          <ul className="home-list">{rows}</ul>
        ) : (
          <p className="home-empty">{loading ? "Loading…" : emptyText}</p>
        )}
      </div>
    </section>
  );
}

function UpdateRow({ item }) {
  const Icon = item.icon;
  return (
    <li className="home-row">
      <span
        className={`home-row-icon home-tone-${item.tone}`}
        aria-hidden="true"
      >
        <Icon size={16} stroke={1.75} />
      </span>
      <div className="home-row-body">
        <span className={`home-row-tag home-tone-${item.tone}`}>
          {item.tag}
        </span>
        <span className="home-row-text">
          <strong>{item.who}</strong>
          {item.text}
        </span>
        {item.sub && <span className="home-row-sub">{item.sub}</span>}
      </div>
    </li>
  );
}

export default HomePage;
